import { useState, useEffect, useRef } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Users, 
  Plus, 
  Search,
  Edit2,
  Trash2,
  Building2,
  Mail,
  Phone,
  MapPin,
  Upload,
  Download,
  FileText
} from 'lucide-react';

export default function ClientsPage() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: '',
    name_ua: '',
    email: '',
    company: '',
    phone: '',
    tax_id: '',
    address: '',
    address_ua: '',
    address_en: '',
    contract_name_ua: '',
    contract_name_en: '',
    contract_number: '',
    contract_date: '',
    service_description_ua: '',
    service_description_en: '',
    currency: 'USD',
    bank_account: '',
    bank_name: '',
    bank_address: '',
    bank_swift: '',
    bank_iban: '',
    corresp_bank_name: '',
    corresp_bank_address: '',
    corresp_bank_swift: ''
  });

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await api.get('/clients');
      setClients(response.data);
    } catch (error) {
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error('Please fill in required fields (Name)');
      return;
    }

    // Clean form data - convert empty strings to null for optional fields
    const cleanedData = {
      ...formData,
      email: formData.email?.trim() || null,
      phone: formData.phone?.trim() || null,
      company: formData.company?.trim() || null,
      name_ua: formData.name_ua?.trim() || null,
      tax_id: formData.tax_id?.trim() || null,
      address_ua: formData.address_ua?.trim() || null,
      address_en: formData.address_en?.trim() || null,
      contract_name_ua: formData.contract_name_ua?.trim() || null,
      contract_name_en: formData.contract_name_en?.trim() || null,
      contract_number: formData.contract_number?.trim() || null,
      contract_date: formData.contract_date?.trim() || null,
      service_description_ua: formData.service_description_ua?.trim() || null,
      service_description_en: formData.service_description_en?.trim() || null,
      bank_account: formData.bank_account?.trim() || null,
      bank_name: formData.bank_name?.trim() || null,
      bank_address: formData.bank_address?.trim() || null,
      bank_swift: formData.bank_swift?.trim() || null,
      bank_iban: formData.bank_iban?.trim() || null,
      corresp_bank_name: formData.corresp_bank_name?.trim() || null,
      corresp_bank_address: formData.corresp_bank_address?.trim() || null,
      corresp_bank_swift: formData.corresp_bank_swift?.trim() || null,
    };

    try {
      if (editingClient) {
        await api.put(`/clients/${editingClient.id}`, cleanedData);
        toast.success('Client updated successfully');
      } else {
        await api.post('/clients', cleanedData);
        toast.success('Client created successfully');
      }
      fetchClients();
      closeModal();
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      if (typeof errorMsg === 'string') {
        toast.error(errorMsg);
      } else if (Array.isArray(errorMsg)) {
        toast.error(errorMsg.map(e => e.msg || JSON.stringify(e)).join(', '));
      } else {
        toast.error('Operation failed');
      }
    }
  };

  const handleDelete = async (clientId) => {
    if (!window.confirm('Are you sure you want to delete this client?')) return;

    try {
      await api.delete(`/clients/${clientId}`);
      toast.success('Client deleted successfully');
      fetchClients();
    } catch (error) {
      toast.error('Failed to delete client');
    }
  };

  const openModal = (client = null) => {
    if (client) {
      setEditingClient(client);
      setFormData({
        name: client.name || '',
        name_ua: client.name_ua || '',
        email: client.email || '',
        company: client.company || '',
        phone: client.phone || '',
        tax_id: client.tax_id || '',
        address: client.address || '',
        address_ua: client.address_ua || '',
        address_en: client.address_en || '',
        contract_name_ua: client.contract_name_ua || '',
        contract_name_en: client.contract_name_en || '',
        contract_number: client.contract_number || '',
        contract_date: client.contract_date || '',
        service_description_ua: client.service_description_ua || '',
        service_description_en: client.service_description_en || '',
        currency: client.currency || 'USD',
        bank_account: client.bank_account || '',
        bank_name: client.bank_name || '',
        bank_address: client.bank_address || '',
        bank_swift: client.bank_swift || '',
        bank_iban: client.bank_iban || '',
        corresp_bank_name: client.corresp_bank_name || '',
        corresp_bank_address: client.corresp_bank_address || '',
        corresp_bank_swift: client.corresp_bank_swift || ''
      });
    } else {
      setEditingClient(null);
      setFormData({
        name: '', name_ua: '', email: '', company: '', phone: '', tax_id: '', address: '',
        address_ua: '', address_en: '',
        contract_name_ua: '', contract_name_en: '', contract_number: '', contract_date: '',
        service_description_ua: '', service_description_en: '', currency: 'USD',
        bank_account: '', bank_name: '', bank_address: '', bank_swift: '', bank_iban: '',
        corresp_bank_name: '', corresp_bank_address: '', corresp_bank_swift: ''
      });
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingClient(null);
  };

  const handleImportCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/clients/import-csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.imported > 0) {
        toast.success(`Imported ${response.data.imported} clients`);
        fetchClients();
      }
      
      if (response.data.total_errors > 0) {
        toast.warning(`${response.data.total_errors} rows had errors`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await api.get('/clients/export-csv', {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'clients_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Clients exported successfully');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const downloadTemplate = () => {
    const headers = [
      'name', 'email', 'company', 'phone', 'address',
      'address_ua', 'address_en',
      'contract_name_ua', 'contract_name_en', 'contract_number', 'contract_date',
      'service_description_ua', 'service_description_en', 'currency',
      'bank_account', 'bank_name', 'bank_address', 'bank_swift', 'bank_iban',
      'corresp_bank_name', 'corresp_bank_address', 'corresp_bank_swift'
    ];
    
    const exampleRow = [
      'Pocketbook International SA', 'billing@pocketbook.com', 'Pocketbook International SA',
      '+41 123 456 789', 'Crocicchio Cortogna 6, 6900 Lugano, Switzerland',
      'Крочікйо Кортонья 6, 6900, Лугано, Швейцарія', 'Crocicchio Cortogna 6, 6900 Lugano, Switzerland',
      'Рамковий договір про надання послуг', 'Master Services Agreement', '01 04/19', '01.04.2019',
      'Плата за послуги дизайну', 'Payment for services of design development', 'USD',
      '26004878946067', 'UkrSibbank', 'Ukraine, Kiev', 'KHABUA2K', 'UA203510050000026004878946067',
      'BNP PARIBAS U.S.A.', '', 'BNPAUS3N'
    ];
    
    const csv = [headers.join(','), exampleRow.join(',')].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'clients_template.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
    
    toast.success('Template downloaded');
  };

  const filteredClients = clients.filter(client =>
    (client.name && client.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (client.email && client.email.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (client.company && client.company.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (client.name_ua && client.name_ua.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (client.contract_number && client.contract_number.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="clients-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Clients</h1>
          <p className="page-subtitle">Manage your client database ({clients.length} clients)</p>
        </div>
        <div className="flex gap-2">
          <input
            type="file"
            accept=".csv"
            onChange={handleImportCSV}
            ref={fileInputRef}
            className="hidden"
          />
          <Button 
            variant="outline"
            onClick={downloadTemplate}
            data-testid="download-template-btn"
          >
            <FileText className="w-4 h-4 mr-2" />
            CSV Template
          </Button>
          <Button 
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            data-testid="import-csv-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            {importing ? 'Importing...' : 'Import CSV'}
          </Button>
          {clients.length > 0 && (
            <Button 
              variant="outline"
              onClick={handleExportCSV}
              data-testid="export-csv-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          )}
          <Button 
            className="bg-[#4F46E5] hover:bg-[#4338CA] text-white" 
            onClick={() => openModal()}
            data-testid="add-client-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Client
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <Input
          type="text"
          placeholder="Search clients..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="search-clients"
        />
      </div>

      {/* Clients Grid */}
      {filteredClients.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredClients.map((client, index) => (
            <Card 
              key={client.id} 
              className="dashboard-card card-hover animate-fade-in"
              style={{ animationDelay: `${index * 0.05}s` }}
              data-testid={`client-card-${client.id}`}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 rounded-lg bg-[#4F46E5]/10 flex items-center justify-center">
                    <Users className="w-6 h-6 text-[#4F46E5]" />
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openModal(client)}
                      className="h-8 w-8 p-0 hover:bg-slate-100"
                      data-testid={`edit-client-${client.id}`}
                    >
                      <Edit2 className="w-4 h-4 text-slate-500" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(client.id)}
                      className="h-8 w-8 p-0 hover:bg-rose-50"
                      data-testid={`delete-client-${client.id}`}
                    >
                      <Trash2 className="w-4 h-4 text-rose-500" />
                    </Button>
                  </div>
                </div>
                
                <h3 className="font-semibold text-lg text-slate-900 mb-1">{client.name}</h3>
                {client.company && (
                  <p className="text-sm text-slate-600 flex items-center gap-1.5 mb-2">
                    <Building2 className="w-4 h-4" />
                    {client.company}
                  </p>
                )}
                
                <div className="space-y-1.5 text-sm text-slate-500">
                  <p className="flex items-center gap-2 truncate">
                    <Mail className="w-4 h-4 flex-shrink-0" />
                    {client.email}
                  </p>
                  {client.contract_number && (
                    <p className="flex items-center gap-2">
                      <FileText className="w-4 h-4 flex-shrink-0" />
                      Contract: {client.contract_number}
                    </p>
                  )}
                </div>
                
                {(client.bank_iban || client.contract_number) && (
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded">
                      Full details saved
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="dashboard-card">
          <CardContent className="py-12">
            <div className="empty-state">
              <div className="empty-state-icon">
                <Users className="w-8 h-8 text-slate-400" />
              </div>
              <p className="empty-state-title">
                {searchQuery ? 'No clients found' : 'No clients yet'}
              </p>
              <p className="empty-state-text">
                {searchQuery 
                  ? 'Try adjusting your search terms' 
                  : 'Add your clients to quickly generate invoices'}
              </p>
              {!searchQuery && (
                <div className="flex gap-2 mt-4">
                  <Button 
                    variant="outline"
                    onClick={downloadTemplate}
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Download CSV Template
                  </Button>
                  <Button 
                    className="bg-[#4F46E5] hover:bg-[#4338CA] text-white"
                    onClick={() => openModal()}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Client
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Modal with Tabs */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto" data-testid="client-modal">
          <DialogHeader>
            <DialogTitle>
              {editingClient ? 'Edit Client' : 'Add New Client'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <Tabs defaultValue="basic" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="basic">Basic Info</TabsTrigger>
                <TabsTrigger value="contract">Contract</TabsTrigger>
                <TabsTrigger value="bank">Bank Details</TabsTrigger>
              </TabsList>
              
              <TabsContent value="basic" className="space-y-4 py-4">
                <div className="form-grid">
                  <div className="form-group">
                    <Label htmlFor="name">Name (English) *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Full name in English"
                    />
                  </div>
                  <div className="form-group">
                    <Label htmlFor="name_ua">Name (Ukrainian)</Label>
                    <Input
                      id="name_ua"
                      value={formData.name_ua}
                      onChange={(e) => setFormData({ ...formData, name_ua: e.target.value })}
                      placeholder="ПІБ українською"
                    />
                  </div>
                </div>
                <div className="form-grid">
                  <div className="form-group">
                    <Label htmlFor="company">Company</Label>
                    <Input
                      id="company"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      placeholder="Company name"
                    />
                  </div>
                  <div className="form-group">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="email@company.com"
                    />
                  </div>
                </div>
                <div className="form-grid">
                  <div className="form-group">
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+1 234 567 890"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <Label htmlFor="tax_id">Tax Payer's Account Number</Label>
                  <Input
                    id="tax_id"
                    value={formData.tax_id}
                    onChange={(e) => setFormData({ ...formData, tax_id: e.target.value })}
                    placeholder="Tax ID / ЄДРПОУ"
                  />
                </div>
                <div className="form-group">
                  <Label htmlFor="address_ua">Address (Ukrainian)</Label>
                  <Textarea
                    id="address_ua"
                    value={formData.address_ua}
                    onChange={(e) => setFormData({ ...formData, address_ua: e.target.value })}
                    placeholder="Адреса українською"
                    rows={2}
                  />
                </div>
                <div className="form-group">
                  <Label htmlFor="address_en">Address (English)</Label>
                  <Textarea
                    id="address_en"
                    value={formData.address_en}
                    onChange={(e) => setFormData({ ...formData, address_en: e.target.value })}
                    placeholder="Address in English"
                    rows={2}
                  />
                </div>
              </TabsContent>
              
              <TabsContent value="contract" className="space-y-4 py-4">
                <div className="form-grid">
                  <div className="form-group">
                    <Label>Contract Name (UA)</Label>
                    <Input
                      value={formData.contract_name_ua}
                      onChange={(e) => setFormData({ ...formData, contract_name_ua: e.target.value })}
                      placeholder="Рамковий договір..."
                    />
                  </div>
                  <div className="form-group">
                    <Label>Contract Name (EN)</Label>
                    <Input
                      value={formData.contract_name_en}
                      onChange={(e) => setFormData({ ...formData, contract_name_en: e.target.value })}
                      placeholder="Master Services Agreement"
                    />
                  </div>
                </div>
                <div className="form-grid">
                  <div className="form-group">
                    <Label>Contract Number</Label>
                    <Input
                      value={formData.contract_number}
                      onChange={(e) => setFormData({ ...formData, contract_number: e.target.value })}
                      placeholder="01 04/19"
                    />
                  </div>
                  <div className="form-group">
                    <Label>Contract Date</Label>
                    <Input
                      value={formData.contract_date}
                      onChange={(e) => setFormData({ ...formData, contract_date: e.target.value })}
                      placeholder="01.04.2019"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <Label>Service Description (UA)</Label>
                  <Input
                    value={formData.service_description_ua}
                    onChange={(e) => setFormData({ ...formData, service_description_ua: e.target.value })}
                    placeholder="Плата за послуги..."
                  />
                </div>
                <div className="form-group">
                  <Label>Service Description (EN)</Label>
                  <Input
                    value={formData.service_description_en}
                    onChange={(e) => setFormData({ ...formData, service_description_en: e.target.value })}
                    placeholder="Payment for services..."
                  />
                </div>
                <div className="form-group">
                  <Label>Currency</Label>
                  <Input
                    value={formData.currency}
                    onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                    placeholder="USD"
                  />
                </div>
              </TabsContent>
              
              <TabsContent value="bank" className="space-y-4 py-4">
                <div className="form-grid">
                  <div className="form-group">
                    <Label>Bank Account</Label>
                    <Input
                      value={formData.bank_account}
                      onChange={(e) => setFormData({ ...formData, bank_account: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <Label>IBAN</Label>
                    <Input
                      value={formData.bank_iban}
                      onChange={(e) => setFormData({ ...formData, bank_iban: e.target.value })}
                      className="font-mono"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <Label>Bank Name</Label>
                  <Input
                    value={formData.bank_name}
                    onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                  />
                </div>
                <div className="form-grid">
                  <div className="form-group">
                    <Label>Bank Address</Label>
                    <Input
                      value={formData.bank_address}
                      onChange={(e) => setFormData({ ...formData, bank_address: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <Label>SWIFT</Label>
                    <Input
                      value={formData.bank_swift}
                      onChange={(e) => setFormData({ ...formData, bank_swift: e.target.value })}
                      className="font-mono"
                    />
                  </div>
                </div>
                <div className="border-t pt-4 mt-4">
                  <p className="text-sm text-slate-500 mb-3">Correspondent Bank (optional)</p>
                  <div className="form-grid">
                    <div className="form-group">
                      <Label>Corresp. Bank Name</Label>
                      <Input
                        value={formData.corresp_bank_name}
                        onChange={(e) => setFormData({ ...formData, corresp_bank_name: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <Label>Corresp. SWIFT</Label>
                      <Input
                        value={formData.corresp_bank_swift}
                        onChange={(e) => setFormData({ ...formData, corresp_bank_swift: e.target.value })}
                        className="font-mono"
                      />
                    </div>
                  </div>
                  <div className="form-group mt-4">
                    <Label>Corresp. Bank Address</Label>
                    <Input
                      value={formData.corresp_bank_address}
                      onChange={(e) => setFormData({ ...formData, corresp_bank_address: e.target.value })}
                      placeholder="City, Country"
                    />
                  </div>
                </div>
              </TabsContent>
            </Tabs>
            
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={closeModal}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-[#4F46E5] hover:bg-[#4338CA] text-white"
                data-testid="save-client-btn"
              >
                {editingClient ? 'Update Client' : 'Add Client'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
